import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import deepEqual from 'fast-deep-equal';
import { OnboardingNavbar } from '../OnboardingNavbar/OnboardingNavbar';
import { OnboardingProgress } from '../OnboardingProgress/OnboardingProgress';
import { Form, Input } from '../../ui';
import { Uploader } from '../../Uploader/Uploader';
import { OnboardingActions } from '../OnboardingActions/OnboardingActions';
import { logout } from '../../../redux/authentication/slice';
import { updateConfig, updateConfigOnboardingSettings } from '../../../redux/config/slice';
import { selectCurrentOrganization } from '../../../redux/authentication/selectors';
import { selectConfig, selectUpdateConfigLoading } from '../../../redux/config/selectors';
import { DEFAULT_RULES } from '../../../consts/formRules';
import { ALLOWED_FILE_TYPES, ATTACHMENT_MAX_SIZES, ONBOARDING_STEPS } from '../../../consts/consts';
import {
  OnboardingHeaderGroup,
  OnboardingContent,
  OnboardingContentGroup,
  OnboardingContentTitle,
  OnboardingContentGroupDescription,
  OnboardingStepWrapper
} from '../../ui/Layout/OnboardingLayout';
import { Heading2, Heading3 } from '../../../components/ui/Typography/Heading';

const OnboardingStepAppearanceComponent = ({
  config,
  updateConfigLoading,
  updateConfig,
  updateConfigOnboardingSettings,
  logout,
  currentOrganization
}) => {
  const [form] = Form.useForm();
  const [isFieldsTouched, setIsFieldsTouched] = useState(false);
  const [tenantLogo, setTenantLogo] = useState(null);
  const [imprint, setImprint] = useState(null);
  const [initialFormData, setInitialFormData] = useState({
    tenant_name: ''
  });

  useEffect(() => {
    if (config && currentOrganization) {
      const initialData = {
        tenant_name: config.tenant_name || ''
      };
      setInitialFormData(initialData);

      form.setFieldsValue({
        tenant_name: config.tenant_name || currentOrganization?.name || ''
      });
      setTenantLogo(config.tenant_logo);
      setImprint(config.imprint);
      setIsFieldsTouched(true);
    }
  }, [config, currentOrganization]);

  const isFormDataEqual = () => {
    if (!isFieldsTouched) {
      return true;
    }
    const currentFormData = form.getFieldsValue();
    const currentData = {
      ...currentFormData,
      tenant_logo: tenantLogo,
      imprint: imprint
    };
    const initialData = {
      ...initialFormData,
      tenant_logo: config?.tenant_logo,
      imprint: config?.imprint
    };
    return deepEqual(currentData, initialData);
  };

  const handleSaveConfig = async (callback, preValidatedValues = null) => {
    try {
      const values = preValidatedValues ?? (await form.validateFields());

      const hasChanges = !isFormDataEqual();

      if (!hasChanges) {
        callback?.();
        return;
      }

      updateConfig({
        config: {
          ...config,
          tenant_name: values.tenant_name,
          tenant_logo: tenantLogo,
          imprint
        },
        callback
      });
    } catch {
      // validation failed
    }
  };

  const handleOnBack = () => {
    handleSaveConfig(() => updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.ORG_EDIT }));
  };

  const handleSaveAndLogout = async () => {
    const hasChanges = !isFormDataEqual();

    if (!hasChanges) {
      logout();
      return;
    }

    try {
      const values = await form.validateFields();
      handleSaveConfig(() => logout(), values);
    } catch {
      logout();
    }
  };

  const handleOnNext = () => {
    handleSaveConfig(() => updateConfigOnboardingSettings({ step: ONBOARDING_STEPS.USERS }));
  };

  const handleLogoUpload = (files) => {
    setTenantLogo(files[0] || null);
    setIsFieldsTouched(Math.random());
  };

  const handleLogoDelete = () => {
    setTenantLogo(null);
    setIsFieldsTouched(Math.random());
  };

  const handleImprintUpload = (files) => {
    setImprint(files[0] || null);
    setIsFieldsTouched(Math.random());
  };

  const handleImprintDelete = () => {
    setImprint(null);
    setIsFieldsTouched(Math.random());
  };

  const getLogoSchema = () => ({
    allowedFileTypes: ALLOWED_FILE_TYPES.image,
    description: 'Quadratische Bilddatei im Format PNG oder JPG mit einer empfohlenen Mindestauflösung von 500×500px',
    maxFiles: 1,
    maxFileSize: ATTACHMENT_MAX_SIZES.default,
    title: 'Logo Ihres Portals'
  });

  const getImprintSchema = () => ({
    allowedFileTypes: ALLOWED_FILE_TYPES.legal,
    title: 'Impressum',
    description:
      'Datei im PDF Format. Das Impressum ist gesetzlich vorgeschrieben und zeigt Ihren Kunden im Footer des Logins, wer das Versicherungsportal betreibt.',
    maxFiles: 1,
    maxFileSize: ATTACHMENT_MAX_SIZES.default
  });

  return (
    <>
      <OnboardingNavbar onLogout={handleSaveAndLogout} loading={updateConfigLoading} />
      <OnboardingProgress />
      <OnboardingContent>
        <OnboardingStepWrapper>
          <OnboardingHeaderGroup>
            <OnboardingContentTitle>
              <Heading2>Individuelle Markenanpassung</Heading2>
            </OnboardingContentTitle>
          </OnboardingHeaderGroup>

          <OnboardingContentGroup className="step-appearance logo">
            <Uploader
              files={tenantLogo ? [tenantLogo] : []}
              onUploadFiles={handleLogoUpload}
              onDeleteFiles={handleLogoDelete}
              schema={getLogoSchema()}
              requireDeleteConfirmation={false}
            />
          </OnboardingContentGroup>

          <OnboardingContentGroup className="step-appearance title">
            <Heading3>Titel Ihres Portals</Heading3>
            <OnboardingContentGroupDescription>
              Anzeige in der Hauptnavigation des Portals
            </OnboardingContentGroupDescription>
            <Form form={form} layout="vertical" onValuesChange={() => setIsFieldsTouched(Math.random())}>
              <Form.Item name="tenant_name" rules={DEFAULT_RULES.INPUT_REQUIRED}>
                <Input />
              </Form.Item>
            </Form>
          </OnboardingContentGroup>

          <OnboardingContentGroup className="step-appearance imprint">
            <Uploader
              files={imprint ? [imprint] : []}
              onUploadFiles={handleImprintUpload}
              onDeleteFiles={handleImprintDelete}
              schema={getImprintSchema()}
              requireDeleteConfirmation={false}
            />
          </OnboardingContentGroup>
        </OnboardingStepWrapper>
        <OnboardingActions onBack={handleOnBack} onNext={handleOnNext} loading={updateConfigLoading} />
      </OnboardingContent>
    </>
  );
};

const mapStateToProps = (state) => ({
  config: selectConfig(state),
  currentOrganization: selectCurrentOrganization(state),
  updateConfigLoading: selectUpdateConfigLoading(state)
});

const mapDispatchToProps = {
  updateConfig,
  updateConfigOnboardingSettings,
  logout
};

export const OnboardingStepAppearance = connect(mapStateToProps, mapDispatchToProps)(OnboardingStepAppearanceComponent);
